import React, { useState } from "react";

import { ActionStatus } from "./ActionStatus";

export const usePerformMultipleActions = <T extends { id: string }>({
    selectedItems,
    isAvailable
}: {
    selectedItems: T[];
    isAvailable: (item: T) => boolean;
}) => {
    const [isActionInProgress, setIsActionInProgress] = useState<boolean>(false);
    const [items, setItems] = useState<Array<T & { processStatus: ActionStatus }>>(
        selectedItems
            .filter((selectedItem) => isAvailable(selectedItem))
            .map((item) => ({ ...item, processStatus: ActionStatus.WaitingForConfirmation }))
    );

    const setStatusById = (id: string, status: ActionStatus) => {
        setItems((prevItems) =>
            prevItems.map((prevItem) => (prevItem.id === id ? { ...prevItem, processStatus: status } : prevItem))
        );
    };

    const performAllItems = async (performAction: (item: T) => Promise<void>) => {
        setIsActionInProgress(true);

        const results = await Promise.allSettled(
            items.map(async (item) => {
                setStatusById(item.id, ActionStatus.InProgress);

                try {
                    await performAction(item);

                    setStatusById(item.id, ActionStatus.Completed);
                } catch (error) {
                    setStatusById(item.id, ActionStatus.Failed);

                    throw error;
                }
            })
        );

        setIsActionInProgress(false);

        return results;
    };

    return {
        items,
        isActionInProgress,
        performAllItems,
        setStatusById,
        setIsActionInProgress
    };
};
